import { useEffect, useState } from "react";
import { ChevronDown, SlidersHorizontal, Ticket } from "lucide-react";
import type { FlightSegment, PlanLeg, PlanTraveler } from "@/lib/types";
import { FlightRow } from "../itinerary/SegmentRow";
import FlightOptionsPanel, { optionsSummary } from "./FlightOptionsPanel";
import LegBookingNotes from "./LegBookingNotes";
import BookTripSheet from "./BookTripSheet";

interface FlightStopProps {
  tripId: string;
  seg: FlightSegment;
  /** The plan's view of the same leg. Absent in print, or before the plan loads. */
  leg?: PlanLeg;
  partySize?: number | null;
  travelers?: PlanTraveler[];
  /** The printed document: the chosen flight and its facts, nothing to press. */
  readOnly?: boolean;
  /** Open the options panel on first render (?leg= / ?view=fares deep links). */
  defaultOpen?: boolean;
}

/**
 * One flight on the timeline: the CHOSEN flight, with a single "Flight options"
 * control that opens everything else about it.
 *
 * `id="leg-<routeId>"` lives here — emails deep-link to it.
 */
export default function FlightStop({
  tripId,
  seg,
  leg,
  partySize,
  travelers,
  readOnly = false,
  defaultOpen = false,
}: FlightStopProps) {
  const [open, setOpen] = useState(defaultOpen);
  const [bookOpen, setBookOpen] = useState(false);
  useEffect(() => {
    if (defaultOpen) setOpen(true);
  }, [defaultOpen]);

  const summary = leg ? optionsSummary(leg) : "";
  const canBook = !!leg && (leg.bookingStatus ?? "none") === "none";

  const footer = leg ? (
    <button
      type="button"
      onClick={() => setOpen((v) => !v)}
      aria-expanded={open}
      aria-controls={"flight-options-" + seg.routeId}
      className="flex min-h-[40px] w-full items-center gap-2 rounded-md border border-dashed border-border px-3 py-1.5 text-left text-xs font-medium text-muted-foreground transition-colors hover:border-[hsl(var(--primary))]/40 hover:text-foreground motion-reduce:transition-none print:hidden"
    >
      <SlidersHorizontal className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <span className="text-foreground">Flight options</span>
      {summary && <span className="min-w-0 truncate">{summary}</span>}
      <ChevronDown
        className={
          "ml-auto h-3.5 w-3.5 shrink-0 transition-transform motion-reduce:transition-none " +
          (open ? "rotate-180" : "")
        }
        aria-hidden="true"
      />
    </button>
  ) : undefined;

  return (
    <div id={"leg-" + seg.routeId} className="scroll-mt-4 space-y-2">
      <FlightRow
        seg={seg}
        readOnly={readOnly}
        partySize={partySize}
        footer={footer}
      />

      {leg && !readOnly && (
        <LegBookingNotes
          leg={leg}
          passengers={partySize}
          onRebook={() => setBookOpen(true)}
        />
      )}

      {open && leg && !readOnly && (
        <>
          <FlightOptionsPanel tripId={tripId} leg={leg} />
          {canBook && (
            <button
              type="button"
              onClick={() => setBookOpen(true)}
              className="inline-flex items-center gap-1 text-sm font-medium text-[hsl(var(--primary))] transition-opacity hover:opacity-80 print:hidden"
            >
              <Ticket className="h-3.5 w-3.5" aria-hidden="true" />
              Book just this flight
            </button>
          )}
        </>
      )}

      {leg && !readOnly && (
        <BookTripSheet
          open={bookOpen}
          onOpenChange={setBookOpen}
          tripId={tripId}
          legs={[leg]}
          passengers={partySize}
          travelers={travelers}
        />
      )}
    </div>
  );
}
